import { useState, useEffect } from "react";
import { T } from "./config/theme.js";
import { canAccess } from "./config/theme.js";
import { api, trackEvent } from "./hooks/useApi.js";
import { Btn, PhaseLogo as PauseLogo } from "./components/ui/index.jsx";
import { Home, Globe, MessageCircle, User } from "lucide-react";
import { EarlybirdBanner, PlanExpiryBanner } from "./components/ui/Banners.jsx";
import { TrialExpiryBanner, TrialExpiryModal } from "./components/ui/TrialAlerts.jsx";
// Sprint 26-1: UTM 추적 시스템
import { captureUtmParams } from "./services/utmService.js";
import { trackPurchase } from "./services/metaPixel.js";

// ── Pages ──
import Landing from "./components/landing/Landing.jsx";
import Login from "./components/pages/Login.jsx";
import Dashboard from "./components/pages/Dashboard.jsx";
import Detail from "./components/pages/Detail.jsx";
import QA from "./components/pages/QA.jsx";
import MarketQA from "./components/pages/MarketQA.jsx";
import Journey from "./components/pages/Journey.jsx";
import MonthlyReport from "./components/pages/MonthlyReport.jsx";
import DividendCalendar from "./components/pages/DividendCalendar.jsx";
import PricingPage from "./components/pages/PricingPage.jsx";
import ProfilePage from "./components/pages/ProfilePage.jsx";
import SupportChatbot from "./components/pages/SupportChatbot.jsx";
import { TermsPage, PrivacyPage, DisclaimerPage, FAQPage } from "./components/pages/LegalPages.jsx";
import GuidePage from "./components/pages/GuidePage.jsx";

// ── Modals ──
import UpgradeModal from "./components/modals/UpgradeModal.jsx";
import CheckoutModal from "./components/modals/CheckoutModal.jsx";
import ActionModal from "./components/modals/ActionModal.jsx";
import OnboardingGoal from "./components/modals/OnboardingGoal.jsx";
// import WaitlistModal from "./components/modals/WaitlistModal.jsx"; // Sprint 21: 웨이트리스트 제거

// ═══════════════════════════════════════════
// App Router
// ═══════════════════════════════════════════
export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem("phase_token") || null);
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("phase_user")); } catch { return null; }
  });
  const [view, setView] = useState(() => localStorage.getItem("phase_token") ? "dash" : "landing");
  const [stock, setStock] = useState(null);
  const [report, setReport] = useState(null);
  const [isSignup, setIsSignup] = useState(false);
  const [showOnboardingGoal, setShowOnboardingGoal] = useState(false);
  const [plan, setPlan] = useState("free");
  const [planExpiresAt, setPlanExpiresAt] = useState(null);
  const [upgradeModal, setUpgradeModal] = useState(null);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [actionModal, setActionModal] = useState(null); // { holding, report, defaultAction }
  // const [showWaitlist, setShowWaitlist] = useState(false); // Sprint 21: 웨이트리스트 제거

  // ★ Sprint 26-1: 앱 시작 시 UTM 파라미터 자동 캡처
  // 광고 클릭 → pause.ai.kr/?utm_source=meta&utm_campaign=chart 진입 시 자동 저장
  // sessionStorage에 저장되어 가입 시 백엔드로 전송됨
  useEffect(() => {
    captureUtmParams();
  }, []);

  // 토큰 변경 시 localStorage 동기화
  useEffect(() => {
    if (token) {
      localStorage.setItem("phase_token", token);
    } else {
      localStorage.removeItem("phase_token");
      localStorage.removeItem("phase_user");
      localStorage.removeItem("phase_refresh_token");
    }
  }, [token]);

  // useApi에서 토큰 자동 갱신 시 App state 동기화
  useEffect(() => {
    const handler = () => {
      const newToken = localStorage.getItem("phase_token");
      if (newToken && newToken !== token) setToken(newToken);
    };
    window.addEventListener("phase-token-refreshed", handler);
    return () => window.removeEventListener("phase-token-refreshed", handler);
  }, [token]);

  useEffect(() => {
    if (user) localStorage.setItem("phase_user", JSON.stringify(user));
  }, [user]);

  // 플랜 로드
  useEffect(() => {
    if (!token) return;
    trackEvent("app.open");
    api("/plan", {}, token).then(data => {
      if (data?.plan) {
        if (data.plan_expires_at && new Date(data.plan_expires_at) < new Date()) {
          setPlan("free");
        } else {
          setPlan(data.plan);
        }
        setPlanExpiresAt(data.plan_expires_at || null);
      }
    }).catch(() => {});
  }, [token]);

  // Handle social login callback (Google/Kakao redirect)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes("access_token")) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");
      if (accessToken) {
        window.location.hash = "";
        setToken(accessToken);
        localStorage.setItem("phase_token", accessToken);
        if (refreshToken) localStorage.setItem("phase_refresh_token", refreshToken);
        if (!localStorage.getItem("phase_onboarded")) {
          setShowOnboardingGoal(true);
        }
        setView("dash");
      }
    }
  }, []);

  // Handle 토스페이먼츠 빌링 인증 콜백
  const [billingStatus, setBillingStatus] = useState(null); // null | "processing" | "done" | "error"
  const [billingError, setBillingError] = useState(null);
  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);

    if (path === "/payment/billing-success") {
      const authKey = params.get("authKey");
      const customerKey = params.get("customerKey");
      const selectedPlan = params.get("plan");
      const selectedCycle = params.get("cycle") || "monthly"; // Sprint 23: 월간/연간
      const currentToken = localStorage.getItem("phase_token");

      if (authKey && currentToken) {
        // URL 정리
        window.history.replaceState({}, "", "/");
        setBillingStatus("processing");

        (async () => {
          try {
            // 1. 빌링키 발급
            await api("/subscription/create-billing", {
              method: "POST",
              body: JSON.stringify({ authKey, customerKey }),
            }, currentToken);

            // 2. 구독 시작 (첫 결제) — Sprint 23: billingCycle 전달
            const result = await api("/subscription/subscribe", {
              method: "POST",
              body: JSON.stringify({ plan: selectedPlan, billingCycle: selectedCycle }),
            }, currentToken);

            if (result?.success) {
              setPlan(result.plan);
              setBillingStatus("done");
              trackEvent("plan.upgrade_click", { meta: { plan: result.plan, price: result.price, cycle: result.billingCycle } });
              // ★ Sprint 26-1: Meta Pixel Purchase 이벤트 (광고 효과 측정 핵심)
              try {
                trackPurchase(result.plan, result.price, result.billingCycle);
              } catch (e) {
                console.error("[META PIXEL] Purchase track 실패:", e);
              }
              setTimeout(() => { setBillingStatus(null); setView("dash"); }, 2500);
            } else {
              throw new Error("구독 시작 실패");
            }
          } catch (err) {
            console.error("[BILLING] 처리 실패:", err);
            setBillingError(err.message);
            setBillingStatus("error");
          }
        })();
      }
    } else if (path === "/payment/billing-fail") {
      window.history.replaceState({}, "", "/");
      setBillingError(params.get("message") || "결제가 취소되었습니다");
      setBillingStatus("error");
    }
  }, []);

  // ── 빌링 처리 상태 오버레이 ──
  if (billingStatus) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Pretendard', sans-serif" }}>
        <div style={{ textAlign: "center", padding: 40 }}>
          {billingStatus === "processing" && (
            <>
              <div style={{ fontSize: 48, marginBottom: 20 }}>💳</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.text }}>결제 처리 중...</div>
              <div style={{ fontSize: 14, color: T.textMuted, marginTop: 8 }}>카드 등록과 첫 결제를 진행하고 있어요</div>
            </>
          )}
          {billingStatus === "done" && (
            <>
              <div style={{ fontSize: 48, marginBottom: 20 }}>✅</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.green || "#22c55e" }}>구독이 시작됐어요!</div>
              <div style={{ fontSize: 14, color: T.textMuted, marginTop: 8 }}>잠시 후 대시보드로 이동합니다...</div>
            </>
          )}
          {billingStatus === "error" && (
            <>
              <div style={{ fontSize: 48, marginBottom: 20 }}>❌</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.red || "#ef4444" }}>결제 실패</div>
              <div style={{ fontSize: 14, color: T.textMuted, marginTop: 12 }}>{billingError || "다시 시도해주세요"}</div>
              <button onClick={() => { setBillingStatus(null); setView("dash"); }} style={{ marginTop: 24, padding: "12px 32px", borderRadius: 10, border: "none", background: T.accent, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                대시보드로 돌아가기
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Pre-auth routes ──
  if (view === "landing") return (
    <>
      <Landing
        onStart={() => { setIsSignup(true); setView("login"); }}
        onLogin={() => { setIsSignup(false); setView("login"); }}
        onTerms={() => setView("terms")}
        onPrivacy={() => setView("privacy")}
        onDisclaimer={() => setView("disclaimer")}
        onFAQ={() => setView("faq")}
      />
      <SupportChatbot />
    </>
  );
  if (view === "terms") return <TermsPage onBack={() => setView("landing")} />;
  if (view === "privacy") return <PrivacyPage onBack={() => setView("landing")} />;
  if (view === "disclaimer") return <DisclaimerPage onBack={() => setView("landing")} />;
  if (view === "faq") return <FAQPage onBack={() => setView("landing")} />;
  if (view === "login" && !token) return (
    <Login
      onLogin={(t, u) => {
        setToken(t); setUser(u);
        localStorage.setItem("phase_token", t);
        if (u) localStorage.setItem("phase_user", JSON.stringify(u));
        if (!localStorage.getItem("phase_onboarded")) setShowOnboardingGoal(true);
        setView("dash");
      }}
      onBack={() => setView("landing")}
      isSignup={isSignup}
    />
  );

  // ── Authenticated shell ──
  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: "'Pretendard', sans-serif" }}>
      <link href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css" rel="stylesheet" />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        .mono-num { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        .label-micro { font-size: 11px; color: ${T.textMuted}; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; }
        @keyframes pulse-dot { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.4); opacity: 0.6; } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        .pulse-anim { animation: pulse 1.5s infinite; }
        @keyframes chipFadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes breathing-gradient { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        .breathing-bg { background: linear-gradient(270deg, #0F1A17, #13201D, #0F1A17); background-size: 200% 200%; animation: breathing-gradient 2s ease infinite; border: 1px solid ${T.accent}40; }
        .bottom-nav { display: none; }
        @media (max-width: 768px) {
          .phase-header { padding: 8px 14px !important; }
          .phase-header-right span { display: none !important; }
          .phase-content { padding: 16px 12px 90px 12px !important; }
          .bottom-nav { display: flex; position: fixed; bottom: 0; left: 0; width: 100%; background: rgba(12,12,20,0.95); backdrop-filter: blur(10px); border-top: 1px solid ${T.border}; z-index: 999; padding-bottom: env(safe-area-inset-bottom); }
          .bottom-tab { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 10px 0; font-size: 11px; color: ${T.textMuted}; cursor: pointer; gap: 3px; transition: color 0.2s; }
          .bottom-tab.active { color: ${T.accent}; font-weight: 700; }
          .bottom-tab-icon { font-size: 20px; }
          .phase-states { overflow-x: auto; flex-wrap: nowrap !important; padding-bottom: 6px; -webkit-overflow-scrolling: touch; }
          .phase-states > div { flex-shrink: 0; }
          .phase-actions { flex-direction: column !important; gap: 8px !important; }
          .phase-actions > div:first-child { width: 100%; }
          .phase-actions > div:first-child h2 { font-size: 18px !important; }
          .phase-action-btns { width: 100%; display: grid !important; grid-template-columns: 1fr 1fr; gap: 6px !important; }
          .phase-action-btns button { width: 100% !important; justify-content: center !important; font-size: 12px !important; padding: 8px 6px !important; }
          .phase-stock-grid { grid-template-columns: 1fr !important; }
          .phase-portfolio-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .phase-detail-grid { grid-template-columns: 1fr !important; }
          .phase-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .phase-compare { flex-direction: column !important; }
          .phase-timeline-item { flex-direction: column !important; align-items: flex-start !important; gap: 4px !important; }
          .phase-card-btns { flex-wrap: wrap !important; }
          .phase-card-btns button { flex: 1 1 45% !important; justify-content: center !important; font-size: 11px !important; }
          .phase-add-form { flex-direction: column !important; }
          .phase-add-form > div { flex: 1 1 100% !important; }
          .phase-landing-grid { grid-template-columns: 1fr !important; }
          .phase-landing-states { grid-template-columns: 1fr 1fr !important; }
          .phase-pricing-grid { grid-template-columns: 1fr 1fr !important; }
          .phase-chat-bubble { max-width: 92% !important; }
          .phase-qa-header { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .phase-qa-btns { width: 100%; justify-content: flex-end !important; }
          .phase-qa-btns button { font-size: 11px !important; }
          .phase-onboarding { padding: 28px 20px !important; }
          .phase-onboarding h3 { font-size: 17px !important; }
          .phase-onboarding-states { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 480px) {
          .phase-kpi-grid { grid-template-columns: 1fr 1fr !important; }
          .phase-portfolio-grid { grid-template-columns: 1fr 1fr !important; }
          .phase-action-btns { grid-template-columns: 1fr 1fr; }
          .phase-pricing-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {showOnboardingGoal && <OnboardingGoal token={token} onComplete={() => { setShowOnboardingGoal(false); localStorage.setItem("phase_onboarded", "true"); }} />}

      <div className="phase-header" style={{ borderBottom: `1px solid ${T.border}`, padding: "10px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div onClick={() => { setView("dash"); setStock(null); }} style={{ cursor: "pointer" }}>
          <PauseLogo size={26} />
        </div>
        <div className="phase-header-right" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: T.textMuted }}>{user?.email}</span>
          <Btn variant="ghost" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => { setView("guide"); }}>가이드</Btn>
          <Btn variant="ghost" style={{ fontSize: 11, padding: "5px 12px", color: T.accent }} onClick={() => { setView("feedback"); }}>피드백</Btn>
          <Btn variant="ghost" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => setView("profile")}>내 계정</Btn>
          <Btn variant="ghost" style={{ fontSize: 11, padding: "5px 12px" }} onClick={() => { setToken(null); setUser(null); setPlan("free"); localStorage.removeItem("phase_token"); localStorage.removeItem("phase_user"); localStorage.removeItem("phase_refresh_token"); setView("landing"); }}>로그아웃</Btn>
        </div>
      </div>

      <div className="phase-content" style={{ maxWidth: 980, margin: "0 auto", padding: "28px 20px" }}>
        {view === "dash" && <>
          <EarlybirdBanner currentPlan={plan} goPricing={() => setView("pricing")} />
          <PlanExpiryBanner token={token} plan={plan} goPricing={() => setView("pricing")} />
          <TrialExpiryBanner token={token} plan={plan} planExpiresAt={planExpiresAt} goPricing={() => setView("pricing")} />
          <TrialExpiryModal plan={plan} planExpiresAt={planExpiresAt} goPricing={() => setView("pricing")} />
          <Dashboard token={token} plan={plan} goDetail={(h, r) => { trackEvent("stock.view", { symbol: h.symbol }); setStock(h); setReport(r); setView("detail"); }} goQA={h => { setStock(h); setView("qa"); }}
            goJourney={() => setView("journey")}
            goMarketQA={() => canAccess(plan, "market_qa") ? setView("marketqa") : setUpgradeModal("market_qa")}
            goMonthly={() => canAccess(plan, "monthly") ? setView("monthly") : setUpgradeModal("monthly")}
            goDividend={() => canAccess(plan, "dividend") ? setView("dividend") : setUpgradeModal("dividend")}
            onUpgrade={f => { trackEvent("plan.upgrade_click", { meta: { feature: f } }); setUpgradeModal(f); }}
          />
        </>}
        {view === "detail" && <Detail holding={stock} report={report} onBack={() => setView("dash")} goQA={h => { setStock(h); setView("qa"); }} token={token} plan={plan} onUpgrade={f => { trackEvent("plan.upgrade_click", { meta: { feature: f } }); setUpgradeModal(f); }} onAction={actionType => setActionModal({ holding: stock, report, defaultAction: actionType })} onDelete={async (id) => { try { await api(`/holdings/${id}`, { method: "DELETE" }, token); trackEvent("holding.delete"); setView("dash"); } catch (e) { alert("삭제 실패: " + e.message); } }} />}

        {actionModal && <ActionModal holding={actionModal.holding} report={actionModal.report} defaultAction={actionModal.defaultAction} onClose={() => setActionModal(null)} onSave={async (data) => {
          // 매매 기록 저장
          try {
            await api("/actions", { method: "POST", body: JSON.stringify(data) }, token);
          } catch {
            const saved = JSON.parse(localStorage.getItem("phase_actions") || "[]");
            saved.unshift(data);
            localStorage.setItem("phase_actions", JSON.stringify(saved));
          }

          // 매수: 평단가 재계산 + 수량 증가
          if (data.action_type === "add_buy" && data.buy_qty && data.avg_price) {
            const h = actionModal.holding;
            const oldTotal = (h.avg_price || 0) * (h.qty || 0);
            const newTotal = data.avg_price * data.buy_qty;
            const totalQty = (h.qty || 0) + data.buy_qty;
            const newAvg = Math.round((oldTotal + newTotal) / totalQty);
            try {
              await api(`/holdings/${h.id}`, { method: "DELETE" }, token);
              await api("/holdings", { method: "POST", body: JSON.stringify({ symbol: h.symbol, name: h.name, avg_price: newAvg, qty: totalQty, prefs: h.prefs }) }, token);
            } catch (e) { console.error("매수 반영 실패:", e); }
          }

          // 매도: 수량 차감 (전량이면 삭제)
          if (data.action_type === "partial_sell" && data.sell_qty) {
            const h = actionModal.holding;
            const remain = (h.qty || 0) - data.sell_qty;
            try {
              if (remain <= 0) {
                await api(`/holdings/${h.id}`, { method: "DELETE" }, token);
                setView("dash");
              } else {
                await api(`/holdings/${h.id}`, { method: "DELETE" }, token);
                await api("/holdings", { method: "POST", body: JSON.stringify({ symbol: h.symbol, name: h.name, avg_price: h.avg_price, qty: remain, prefs: h.prefs }) }, token);
              }
            } catch (e) { console.error("매도 반영 실패:", e); }
          }

          setActionModal(null);
        }} />}
        {view === "qa" && <QA token={token} holding={stock} onBack={() => setView("dash")} />}
        {view === "marketqa" && <MarketQA token={token} onBack={() => setView("dash")} plan={plan} />}
        {view === "journey" && <Journey token={token} onBack={() => setView("dash")} />}
        {view === "monthly" && <MonthlyReport token={token} onBack={() => setView("dash")} />}
        {view === "dividend" && <DividendCalendar token={token} onBack={() => setView("dash")} />}
        {view === "pricing" && <PricingPage onBack={() => setView("dash")} currentPlan={plan} onSelectPlan={p => { if (p === "free") return; setCheckoutPlan(p); }} />}
        {view === "profile" && <ProfilePage user={user} onBack={() => setView("dash")} goPricing={() => setView("pricing")} currentPlan={plan} />}
        {view === "terms_in" && <TermsPage onBack={() => setView("dash")} />}
        {view === "privacy_in" && <PrivacyPage onBack={() => setView("dash")} />}
        {view === "disclaimer_in" && <DisclaimerPage onBack={() => setView("dash")} />}
        {view === "faq_in" && <FAQPage onBack={() => setView("dash")} />}
        {view === "guide" && <GuidePage onBack={() => setView("dash")} token={token} initialTab="guide" />}
        {view === "feedback" && <GuidePage onBack={() => setView("dash")} token={token} initialTab="feedback" />}

        {upgradeModal && <UpgradeModal feature={upgradeModal} onClose={() => setUpgradeModal(null)} goPricing={() => { setUpgradeModal(null); setView("pricing"); }} />}
        {checkoutPlan && <CheckoutModal plan={checkoutPlan} currentPlan={plan} token={token} onClose={() => setCheckoutPlan(null)} onSuccess={(newPlan) => { setPlan(newPlan); setCheckoutPlan(null); setView("dash"); }} />}
      </div>

      {/* ─── 모바일 하단 탭 바 (Bottom Navigation) ─── */}
      <div className="bottom-nav">
        <div className={`bottom-tab ${view === "dash" ? "active" : ""}`} onClick={() => { setView("dash"); setStock(null); }}>
          <Home size={20} strokeWidth={1.5} /><span>홈</span>
        </div>
        <div className={`bottom-tab ${view === "marketqa" ? "active" : ""}`} onClick={() => canAccess(plan, "market_qa") ? setView("marketqa") : setUpgradeModal("market_qa")}>
          <Globe size={20} strokeWidth={1.5} /><span>시장분석</span>
        </div>
        <div className={`bottom-tab ${view === "qa" ? "active" : ""}`} onClick={() => { if (stock) setView("qa"); else alert("홈에서 종목 카드의 'AI 상담' 버튼을 먼저 눌러주세요."); }}>
          <MessageCircle size={20} strokeWidth={1.5} /><span>AI상담</span>
        </div>
        <div className={`bottom-tab ${view === "profile" ? "active" : ""}`} onClick={() => setView("profile")}>
          <User size={20} strokeWidth={1.5} /><span>내정보</span>
        </div>
      </div>

      <footer style={{ borderTop: `1px solid ${T.border}`, padding: "20px 24px", textAlign: "center", marginTop: 40 }}>
        <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
          {[
            { label: "이용약관", v: "terms_in" },
            { label: "개인정보처리방침", v: "privacy_in" },
            { label: "투자 위험 고지", v: "disclaimer_in" },
            { label: "사용 가이드", v: "guide" },
            { label: "FAQ", v: "faq_in" },
          ].map((l, i) => (
            <span key={i} onClick={() => setView(l.v)} style={{ fontSize: 11, color: T.textMuted, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
              onMouseEnter={e => e.target.style.color = T.accent}
              onMouseLeave={e => e.target.style.color = T.textMuted}
            >{l.label}</span>
          ))}
        </div>
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 10, opacity: 0.5 }}>© 2026 Pause. All rights reserved.</div>
      </footer>
      <SupportChatbot />
    </div>
  );
}
